import { ApiClient } from '@/config/axios.config';
import type {
  Port,
  Purpose,
  ViaPoint,
  ReferenceDataItem,
  ReferenceDataType,
} from '@/types/referenceData.types';

class ReferenceDataService {
  private baseUrl = '/reference-data';

  /**
   * Fetch all ports from the API
   */
  async getPorts(): Promise<Port[]> {
    try {
      return await ApiClient.get<Port[]>(`${this.baseUrl}/ports`);
    } catch (error) {
      console.error('Error fetching ports:', error);
      throw new Error('Failed to fetch ports');
    }
  }

  /**
   * Fetch all purposes from the API
   */
  async getPurposes(): Promise<Purpose[]> {
    try {
      return await ApiClient.get<Purpose[]>(`${this.baseUrl}/purposes`);
    } catch (error) {
      console.error('Error fetching purposes:', error);
      throw new Error('Failed to fetch purposes');
    }
  }

  /**
   * Fetch all via points from the API
   */
  async getViaPoints(): Promise<ViaPoint[]> {
    try {
      return await ApiClient.get<ViaPoint[]>(`${this.baseUrl}/viapoints`);
    } catch (error) {
      console.error('Error fetching via points:', error);
      throw new Error('Failed to fetch via points');
    }
  }

  /**
   * Generic method to fetch reference data by type
   */
  async getReferenceData(type: ReferenceDataType): Promise<ReferenceDataItem[]> {
    switch (type) {
      case 'ports':
        return this.getPorts();
      case 'purposes':
        return this.getPurposes();
      case 'viapoints':
        return this.getViaPoints();
      default:
        throw new Error(`Unknown reference data type: ${type}`);
    }
  }

  /**
   * Search reference data by name (client-side filtering)
   */
  async searchReferenceData(
    type: ReferenceDataType,
    query: string,
    limit = 10
  ): Promise<ReferenceDataItem[]> {
    const data = await this.getReferenceData(type);

    if (!query.trim()) {
      return data.slice(0, limit);
    }

    const filtered = data.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));

    return filtered.slice(0, limit);
  }
}

export const referenceDataService = new ReferenceDataService();
